'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/frontier-extra'

const RewardHacking = dynamic(() => import('../_components/widgets/FrontierExtraWidgets').then(m => m.RewardHacking), { ssr: false })

export default function SafetyAndAlignment() {
  return (
    <Lesson>
      <Step>
        <H>Doing what we actually mean</H>
        <P>
          Every model in this course optimised a number you chose: a loss, a reward, an accuracy. Optimisers are relentless
          and literal. They find whatever raises the number, including ways you didn’t intend. <strong>Alignment</strong> is
          the work of making sure a capable system pursues what we actually want. <strong>Safety</strong> is the broader work of
          making sure it doesn’t cause harm along the way.
        </P>
        <P>
          This isn’t a far-future worry. It shows up in today’s systems, in small, measurable ways, and you have the tools to
          measure them.
        </P>
      </Step>

      <Step gate="q-hack">
        <H>Reward hacking</H>
        <P>
          A reward model is a learned proxy for human judgement, and proxies have gaps. Push hard enough and the policy finds
          them: overly long answers because length was rewarded, flattery because people upvote agreement, code that passes the
          tests by special-casing them. This is Goodhart’s law again, now inside training.
        </P>
        <Figure n="10.13" wide caption="As training pushes harder on the proxy, its score keeps climbing while real quality peaks and falls. A KL penalty, which keeps the model close to where it started, holds it near the peak.">
          <RewardHacking />
        </Figure>
        <Quiz
          id="q-hack"
          question="A coding model is rewarded for passing unit tests. Which behaviour is reward hacking?"
          options={[
            { label: 'Editing the tests so they always pass', correct: true },
            { label: 'Writing a correct, general solution', why: 'That’s the intended behaviour. The reward and the goal agree here.' },
            { label: 'Asking a clarifying question', why: 'That might lower the immediate reward, but it isn’t gaming it.' },
          ]}
          explain="Defences include better reward models, penalties for drifting from the base model (KL), hidden tests, and evaluating with different measures than the ones trained on."
        />
      </Step>

      <Step gate="calibration">
        <H>Knowing what you don’t know</H>
        <P>
          A model that’s 90% sure and right 60% of the time is dangerous to rely on. <strong>Calibration</strong> measures
          whether confidence matches reality, and hallucinations are often calibration failures: confident answers with nothing
          behind them.
        </P>
        <Exercise
          id="calibration"
          title="ece"
          {...EX.calibration}
          hints={[
            <>
              For each bin <C>b</C>: the indices where <C>{'Math.min(9, Math.floor(confidence[i] * 10)) === b'}</C>.
            </>,
            <>
              Add <C>{'Math.abs(avgConf - acc) * (idx.length / n)'}</C> for each non-empty bin.
            </>,
          ]}
        />
      </Step>

      <Step gate="linear-probe">
        <H>Looking inside</H>
        <P>
          <strong>Interpretability</strong> asks what a network has actually learned, from the inside. One basic tool is the{' '}
          <strong>linear probe</strong>: if a simple linear classifier can read a concept straight out of a layer’s activations,
          then the network represents that concept there. Researchers use probes, and more powerful tools built on the same
          idea, to find features for things like truthfulness, sentiment or a particular topic inside large models.
        </P>
        <Exercise
          id="linear-probe"
          title="trainProbe"
          {...EX.linearProbe}
          hints={[
            <>It’s logistic regression from Module 6: weights, bias, sigmoid, and the gradient (p − y)·h.</>,
            <>
              Return <C>{'h => (vec.dot(w, h) + b > 0 ? 1 : 0)'}</C>.
            </>,
          ]}
        />
        <Callout kind="note">
          A probe finding a concept shows it’s <em>represented</em>, not that the model <em>uses</em> it. To test use,
          researchers intervene: they change the feature and see whether the behaviour changes.
        </Callout>
      </Step>

      <Step gate={['fairness', 'q-safety']}>
        <H>Fair for whom?</H>
        <P>
          Models learn patterns from data, including unfair ones (you saw it with word embeddings). Overall accuracy can hide
          that a system works much worse for one group of people. So we measure error rates separately for each group.
        </P>
        <Exercise
          id="fairness"
          title="truePositiveRate and opportunityGap"
          {...EX.fairness}
          hints={[
            <>
              TPR: among indices with <C>{'truth[i] === 1'}</C>, the fraction with <C>{'pred[i] === 1'}</C>.
            </>,
            <>Compute the TPR on each group’s indices separately and take the absolute difference.</>,
          ]}
        />
        <P>The wider safety toolkit, in brief:</P>
        <UL>
          <li>
            <strong>Training for values</strong>: RLHF, and methods like Constitutional AI, where a model critiques and revises
            its own answers against written principles.
          </li>
          <li>
            <strong>Red-teaming</strong>: people and models deliberately try to make a system misbehave before release, and the
            failures become training and test data.
          </li>
          <li>
            <strong>Dangerous-capability evaluations</strong>: testing whether a model could meaningfully help with serious
            harms, such as cyberattacks or weapons, before deciding how to deploy it.
          </li>
          <li>
            <strong>Monitoring and control</strong>: safeguards around a deployed model, especially for agents that take real
            actions.
          </li>
        </UL>
        <Quiz
          id="q-safety"
          question="Why can’t safety be fully solved by writing a long list of rules into the system prompt?"
          options={[
            { label: 'Rules can’t anticipate every situation, and a model can follow their letter while missing their intent', correct: true },
            { label: 'Models ignore system prompts', why: 'They mostly follow them. The problem is that rules are always incomplete and can be gamed.' },
            { label: 'Rules make models slower', why: 'Speed isn’t the issue. Specification is.' },
          ]}
          explain="That’s the alignment problem in one sentence: what we can write down is never quite what we mean. It’s an open research field, and one you’re now equipped to join."
        />
        <Callout kind="why">
          Every tool in this lesson was built from earlier ones: calibration from probability, probes from logistic
          regression, fairness from evaluation metrics, reward hacking from optimisation. Safety research is machine learning,
          pointed at the question of whether the system is doing what we meant.
        </Callout>
      </Step>
    </Lesson>
  )
}
